import { useEffect, useRef } from 'react';
import { actions } from '../reducers';
import { getNotes } from './notesUtils';

import setup from '../setup';

const { fetchArraySize } = setup;

type NoteItem = {
  id: number;
  header: string;
  body: string;
}

type RequestError = {
  error: boolean;
  message: string;
}


export function useFetchNotes(
  dispatchNotesList: React.Dispatch<{ type: string, payload: NoteItem[] }>,
  dispatchShortList: React.Dispatch<{ type: string, payload: NoteItem[] | string}>,
  dispatchOnLine: React.Dispatch<{ type: string, payload: boolean }>,
  notesList: NoteItem[],
  token: string,
  userId: number,
  search = '',
  deleted = false,
  localDB: IDBDatabase | null,
) {
  // checkList ensures that new notes would append only once
  const checkList: React.MutableRefObject<number[]> = useRef([]);
  const downloadedNotes: React.MutableRefObject<NoteItem[]> = useRef([]);
  useEffect(() => {
    async function fetchData(from: number, num: number, search = '', deleted = false) {
      try {
        const array: NoteItem[] | RequestError = await getNotes(token, from, num, search, deleted);
        if (!Array.isArray(array)) {
          throw new Error('incorrect response from api');
        }
        switch (true) {
          case checkList.current.includes(from): break; // duplicate function call
          case from === 0: downloadedNotes.current = [...(array as NoteItem[])]; break; // first data append
          default: downloadedNotes.current = [...downloadedNotes.current, ...(array as NoteItem[])]; break; // add new portion
        }
        checkList.current.push(from);
        if (array.length < num) {
          return true;
        }
        await fetchData(from + num, num, search, deleted);
        return true;
      } catch (err) {
        return Promise.reject(err);
      }
    }

  async function fetchLocal(): Promise<NoteItem[]> {
    if (localDB === null) return Promise.resolve([]);
    const table = deleted ? 'deleted' : 'notes';
    const trans = localDB.transaction(table, 'readonly');
    const objectStore = trans.objectStore(table);
    const request = objectStore.index('datetime').getAll();
    
    return new Promise((resolve, reject) => {
      request.onsuccess = () => {
        const array = request.result;
        const filtered = array.filter((x) => x.user === userId).reverse();
        resolve(filtered);
      };
      request.onerror = () => {
        resolve([]);
      };
    });
  }

    async function dumpDb(list: NoteItem[]) {
      try {
        if (localDB === null) return;
        if (list.length === 0) return;
        const table = deleted ? 'deleted' : 'notes';
        const trans = localDB.transaction(table, 'readwrite');
        const objectStore = trans.objectStore(table);
        objectStore.clear();
        list.forEach((item) => objectStore.put(item));
      } catch (err) {
        return Promise.reject(err);
      }

    }

    async function fetchBoth() {
      try {
        // set onLine = false
        dispatchOnLine(actions.setup(false));

        // first fetch data from local db
        const list = await fetchLocal();
        dispatchNotesList(actions.resetNotes(list))
        // return;
        // fetch data from remote
        // if connection is poor user sees disabled local dump
        checkList.current = [];
        downloadedNotes.current = [];
        const result = await fetchData(0, fetchArraySize, '', deleted);
        
        if (result) {
          dispatchNotesList(actions.resetNotes(downloadedNotes.current));
          dispatchOnLine(actions.setup(true));
          await dumpDb(downloadedNotes.current);
        }
      } catch (err) {
        console.log((err as Error).message);
      }

    };
    fetchBoth();
  }, [deleted, dispatchShortList, dispatchOnLine, localDB, token, userId, dispatchNotesList]);

  // filter notes by search string
  useEffect(() => {
    // console.log('useNotesList', notesList);
    dispatchShortList(actions.resetNotes(notesList));
    // dispatchShortList(actions.appendNotes(notesList));
    dispatchShortList(actions.searchNotes(search as string));
  }, [dispatchShortList, notesList, search]);
}

